import React, {
  useCallback, useContext, useEffect, useState,
} from 'react'
import Offcanvas from 'react-bootstrap/Offcanvas'
import { useTranslation } from 'react-i18next'
import { Consignment } from 'features/declaration/consignment/models/types'
import { TransitOperation } from 'features/declaration/transit-operation/models/types'
import { UserContext } from 'shared/users/contexts/UserContext'
import { SecurityEnum } from 'features/declaration/models/schemas/declarationEnums'
import { AddressFormValues } from 'features/declaration/models/schemas/commonConsignmentSchemas'
import { concatStreetAndNumber, hasText, sortBySequenceNumber } from 'shared/lib/common-util'
import useCodelist, { createDepartureCustomsOfficesByDeclarationTypeAndUserFilter } from 'features/declaration/codelist/api/useCodelist'
import { ADDITIONAL_DECLARATION_TYPE_OPTIONS, DECLARATION_TYPE_OPTIONS } from 'features/declaration/models/declarationOptions'
import TransitPreviewSection from './TransitPreviewSection'
import TransitPreviewField from './TransitPreviewField'
import TransitPreviewArrayField from './TransitPreviewArrayField'
import TransitPreviewFeedbackSection from './TransitPreviewFeedbackSection'
import TransitPreviewAuditSection from './TransitPreviewAuditSection'
import HouseConsignmentsTable from './HouseConsignmentsTable'
import { useFetchConsignmentDocuments } from '../../consignment-document/api/fetchConsignmentDocuments'
import { Guarantee } from '../../guarantee/models/types'
import { useFetchConsignment } from '../../consignment/api/fetchConsignment'
import { useFetchGuarantees } from '../../guarantee/api/fetchGuarantees'
import { useFetchHouseConsignments } from '../../house-consignment/api/fetchHouseConsignments'
import TransitPreviewSupportingDocuments from './TransitPreviewSupportingDocuments'
import { isPreviousDocument, isSupportingDocument, isTransportDocument } from '../../document/models/types'
import TransitPreviewPreviousDocuments from './TransitPreviewPreviousDocuments'
import TransitPreviewTransportDocuments from './TransitPreviewTransportDocuments'
import TransitPreviewDocumentsSection from './TransitPreviewDocumentsSection'
import { CreateOrUpdateTransitOperationRequestDeclarationTypeEnum } from '../../../../api/models'

const securityOptions = [
  {
    value: SecurityEnum.Enum['0'],
    label: '0 - Not used for safety and security purposes',
  },
  {
    value: SecurityEnum.Enum['1'],
    label: '1 - ENS',
  },
  {
    value: SecurityEnum.Enum['2'],
    label: '2 - EXS',
  },
  {
    value: SecurityEnum.Enum['3'],
    label: '3 - ENS & EXS',
  },
]

interface TransitPreviewDetailsProps {
  transitOperationId: number
  isLoading: boolean
  transitOperation: TransitOperation | undefined
}

function TransitPreviewDetails(props: Readonly<TransitPreviewDetailsProps>) {
  const { t } = useTranslation()
  // const { user } = useContext(UserContext)
  const {
    transitOperationId,
    isLoading,
    transitOperation,
  } = props

  const {
    user,
  } = useContext(UserContext)

  const [consignmentData, setConsignmentData] = useState<Consignment>()
  // const [authorisationsData, setAuthorisationsData] = useState<AuthorisationResponse[]>()
  const [guaranteeData, setGuaranteeData] = useState<Guarantee[]>()
  // const [directoEventData, setDirectoEventData] = useState<DirectoEventResponse>()
  const [, countriesOptions] = useCodelist('COUNTRIES')
  const departureCustomsOfficesFilterFn = useCallback(
    () => createDepartureCustomsOfficesByDeclarationTypeAndUserFilter(transitOperation?.declarationType, user),
    [transitOperation?.declarationType, user],
  )
  const [, departureCustomsOfficesOptions] = useCodelist('DEPARTURE_CUSTOMS_OFFICES', departureCustomsOfficesFilterFn)
  const [, destinationCustomsOfficesOptions] = useCodelist('DESTINATION_CUSTOMS_OFFICES')

  const [, transportModeOptions] = useCodelist('TRANSPORT_MODES')

  const { data: consignment, isLoading: isConsignmentLoading } = useFetchConsignment(transitOperationId)
  const { data: consignmentDocumentsData, isLoading: isConsignmentDocumentLoading } = useFetchConsignmentDocuments(consignment?.id ?? null)
  const { data: guarantees, isLoading: isGuaranteesLoading } = useFetchGuarantees(transitOperationId)
  const { data: houseConsignmentsData, isLoading: isHouseConsignmentsLoading } = useFetchHouseConsignments(consignment?.id ?? null)

  const consignmentDocuments = consignmentDocumentsData ?? []
  const houseConsignments = houseConsignmentsData ?? []

  // const { fetchDirectoEvent } = useDirectoEventApi(transitOperationId, user?.role === 'ADMIN')
  useEffect(() => {
    if (isConsignmentLoading) return
    setConsignmentData(consignment)
  }, [consignment, isConsignmentLoading])

  useEffect(() => {
    if (!guarantees) return
    setGuaranteeData(guarantees)
  }, [guarantees])

  const previousDocuments = consignmentDocuments.filter(isPreviousDocument).sort(sortBySequenceNumber)
  const transportDocuments = consignmentDocuments.filter(isTransportDocument).sort(sortBySequenceNumber)
  const supportingDocuments = consignmentDocuments.filter(isSupportingDocument).sort(sortBySequenceNumber)

  const getStreetAndNumber = (addressResponse?: AddressFormValues | null): string => {
    if (!addressResponse) return ''

    const shouldUseStreet = transitOperation?.declarationType === CreateOrUpdateTransitOperationRequestDeclarationTypeEnum.ENS
      || ['1', '3'].includes(transitOperation?.security ?? '')

    if (shouldUseStreet) {
      return concatStreetAndNumber(addressResponse.street ?? '', addressResponse.streetNumber)
    }

    return addressResponse.addressLine ?? ''
  }

  const initialLoading = isLoading || isConsignmentLoading || isConsignmentDocumentLoading
    || isGuaranteesLoading || isHouseConsignmentsLoading

  return (
    <Offcanvas.Body key="data-body">
      {
        hasText(transitOperation?.auditReason) && (
          <TransitPreviewAuditSection auditReason={transitOperation?.auditReason ?? ''} />
        )
      }
      {
        hasText(transitOperation?.feedback) && (
          <TransitPreviewFeedbackSection feedback={transitOperation?.feedback ?? ''} />
        )
      }
      <TransitPreviewSection
        columnId="general-info"
        headingLabel={t('declaration.navbar.general')}
      >
        <div className="col-6">
          <TransitPreviewField
            label={t('declaration.p5.security')}
            value={securityOptions.find((option) => option.value === transitOperation?.security)?.label ?? '0'}
          />
          <TransitPreviewField
            label={t('declaration.p5.departureCustomsOffice')}
            value={departureCustomsOfficesOptions.find(
              (option) => option.value === transitOperation?.departureCustomsOffice,
            )?.label}
          />
          <TransitPreviewField
            label={t('declaration.p5.destinationCustomsOffice')}
            value={destinationCustomsOfficesOptions.find(
              (option) => option.value === transitOperation?.destinationCustomsOffice,
            )?.label}
          />
          {
            user?.role === 'ADMIN' && (
              <TransitPreviewField
                label={t('transit.guaranteeClaimReservationStatus')}
                isLoading={isLoading}
                value={transitOperation?.guaranteeClaimReservationStatus}
              />
            )
          }
        </div>
        <div className="col-6">
          <TransitPreviewField
            label={t('declaration.p5.declarationType')}
            isLoading={isConsignmentDocumentLoading}
            value={DECLARATION_TYPE_OPTIONS.find((option) => option.value === transitOperation?.declarationType)?.label}
          />
          <TransitPreviewField
            label={t('declaration.p5.additionalDeclarationType')}
            isLoading={isConsignmentDocumentLoading}
            value={ADDITIONAL_DECLARATION_TYPE_OPTIONS
              .find((option) => option.value === transitOperation?.additionalDeclarationType)?.label}
          />
          <TransitPreviewField
            label={t('declaration.p5.bindingItinerary')}
            value={transitOperation?.bindingItinerary === 'false' ? t('common.no') : t('common.yes')}
          />

          <TransitPreviewArrayField
            label={t('declaration.p5.countriesOfRouting')}
            isLoading={isConsignmentDocumentLoading}
            items={consignmentData?.countryOfRoutingOfConsignment.sort(sortBySequenceNumber).map((routeCountry) => ({
              value: countriesOptions.find((option) => option.value === routeCountry.country)?.label,
              parenthesesValue: null,
            })) ?? []}
          />
        </div>
      </TransitPreviewSection>
      <TransitPreviewSection
        columnId="consignment"
        headingLabel={t('declaration.navbar.consignment')}
      >
        <div className="col-12 row m-0">
          <div className="col-6">
            <TransitPreviewField
              label={t('declaration.p5.placeOfLoading')}
              isLoading={isConsignmentDocumentLoading}
              value={consignmentData?.placeOfLoading.location ?? '-'}
              parenthesesValue={countriesOptions.find((option) => option.value === consignmentData?.placeOfLoading.country)?.label ?? '-'}
            />
            <TransitPreviewField
              label={t('declaration.p5.placeOfUnLoading')}
              isLoading={isConsignmentDocumentLoading}
              value={consignmentData?.placeOfUnloading?.location ?? '-'}
              parenthesesValue={countriesOptions.find((option) => option.value === consignmentData?.placeOfUnloading?.country)?.label ?? '-'}
            />
            <TransitPreviewField
              label={t('declaration.p5.grossWeight')}
              isLoading={isConsignmentDocumentLoading}
              value={`${consignmentData?.grossMass ?? 0}`}
            />
            <TransitPreviewField
              label={t('declaration.guaranteeAmount')}
              isLoading={isGuaranteesLoading}
              value={`${guaranteeData?.at(0)?.amount ?? '-'}`}
            />
          </div>
          <div className="col-6">
            <TransitPreviewField
              label={t('declaration.p5.inlandTransportMode')}
              isLoading={isConsignmentDocumentLoading}
              value={transportModeOptions.find((option) => option.value === consignmentData?.inlandModeOfTransport)?.label ?? '-'}
            />
            <TransitPreviewArrayField
              label={t('declaration.p5.departureTransportMeans')}
              isLoading={isConsignmentDocumentLoading}
              items={consignmentData?.departureTransportMeans.sort(sortBySequenceNumber).map((transportMeans) => ({
                value: transportMeans.identificationNumber,
                parenthesesValue: countriesOptions.find((option) => option.value === transportMeans.nationality)?.label,
              })) ?? []}
            />
            <TransitPreviewField
              label={t('declaration.p5.borderTransportMode')}
              isLoading={isConsignmentDocumentLoading}
              value={transportModeOptions.find((option) => option.value === consignmentData?.borderModeOfTransport)?.label ?? '-'}
            />
            <TransitPreviewArrayField
              label={t('declaration.p5.borderTransportMeans')}
              isLoading={isConsignmentDocumentLoading}
              items={consignmentData?.activeBorderTransportMeans.sort(sortBySequenceNumber).map((transportMeans) => ({
                value: transportMeans.identificationNumber,
                parenthesesValue: countriesOptions.find((option) => option.value === transportMeans.nationality)?.label,
              })) ?? []}
            />
          </div>
          <div className="d-flex flex-wrap">
            <div className="col-6">
              {transportDocuments.length > 0 && (
                <TransitPreviewDocumentsSection>
                  <TransitPreviewTransportDocuments transportDocuments={transportDocuments} />
                </TransitPreviewDocumentsSection>
              )}
              {previousDocuments.length > 0 && (
                <TransitPreviewDocumentsSection>
                  <TransitPreviewPreviousDocuments previousDocuments={previousDocuments} />
                </TransitPreviewDocumentsSection>
              )}
            </div>
            <div className="col-6">
              {supportingDocuments.length > 0 && (
                <TransitPreviewDocumentsSection>
                  <TransitPreviewSupportingDocuments supportingDocuments={supportingDocuments} />
                </TransitPreviewDocumentsSection>
              )}
            </div>
          </div>
        </div>
      </TransitPreviewSection>
      <TransitPreviewSection
        columnId="transitParties"
        headingLabel={t('declaration.navbar.transitParties')}
      >
        <div className="col-6">
          <TransitPreviewArrayField
            label={t('declaration.p5.consignor')}
            isLoading={isConsignmentLoading}
            items={[
              {
                value: consignmentData?.consignor?.name,
                parenthesesValue: consignmentData?.consignor?.identificationNumber ?? null,
              },
              {
                value: getStreetAndNumber(consignmentData?.consignor?.address),
                parenthesesValue: consignmentData?.consignor?.address?.city,
              },
              {
                value: countriesOptions.find((option) => option.value === consignmentData?.consignor?.address?.country)?.label,
                parenthesesValue: consignmentData?.consignor?.address?.postcode,
              },
            ]}
          />
          <TransitPreviewArrayField
            label={t('declaration.p5.carrier')}
            isLoading={isConsignmentLoading}
            items={[
              {
                value: consignmentData?.carrier?.name,
                parenthesesValue: consignmentData?.carrier?.identificationNumber ?? null,
              },
              {
                value: getStreetAndNumber(consignmentData?.carrier?.address),
                parenthesesValue: consignmentData?.carrier?.address?.city,
              },
              {
                value: countriesOptions.find((option) => option.value === consignmentData?.carrier?.address?.country)?.label,
                parenthesesValue: consignmentData?.carrier?.address?.postcode,
              },
            ]}
          />

        </div>
        <div className="col-6">
          <TransitPreviewArrayField
            label={t('declaration.p5.consignee')}
            isLoading={isConsignmentLoading}
            items={[
              {
                value: consignmentData?.consignee?.name,
                parenthesesValue: consignmentData?.consignee?.identificationNumber ?? null,
              },
              {
                value: getStreetAndNumber(consignmentData?.consignee?.address),
                parenthesesValue: consignmentData?.consignee?.address?.city,
              },
              {
                value: countriesOptions.find((option) => option.value === consignmentData?.consignee?.address?.country)?.label,
                parenthesesValue: consignmentData?.consignee?.address?.postcode,
              },
            ]}
          />
        </div>
      </TransitPreviewSection>
      <TransitPreviewSection
        columnId="houseConsignments"
        headingLabel={t('declaration.navbar.houseConsignments', { count: houseConsignments.length })}
      >
        {consignment?.id && (
          <HouseConsignmentsTable
            consignmentId={consignment.id}
            houseConsignments={houseConsignments}
            consignmentConsignorName={consignmentData?.consignor?.name ?? null}
            consignmentConsigneeName={consignmentData?.consignee?.name ?? null}
            isLoading={isHouseConsignmentsLoading || initialLoading}
          />
        )}
      </TransitPreviewSection>
    </Offcanvas.Body>
  )
}

export default TransitPreviewDetails
